import type { BugReportContext } from '../../shared/types/support'
import {
	APP_VERSION,
	BUNDLE_COMMIT,
	IS_NATIVE_APP,
} from '../config/environment'

let lastAppRoute: string | undefined

/** Remembers the last non-settings route so bug reports show where the issue happened. */
export function rememberAppRoute(path: string): void {
	if (path === '/settings' || path.startsWith('/setting/')) return
	lastAppRoute = path
}

/** Collects device and app details for a bug report; never blocks submission. */
export async function collectBugReportContext(): Promise<BugReportContext> {
	const native = IS_NATIVE_APP ? await readNativeAppInfo() : undefined
	const ratio = Math.round(window.devicePixelRatio * 100) / 100
	return {
		appBuild: native?.build,
		appVersion: native?.version ?? APP_VERSION,
		bundleCommit: BUNDLE_COMMIT,
		colorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches
			? 'dark'
			: 'light',
		displayMode: window.matchMedia('(display-mode: standalone)').matches
			? 'standalone'
			: 'browser',
		locale: navigator.language,
		online: navigator.onLine,
		platform: IS_NATIVE_APP ? 'ios' : 'web',
		route: lastAppRoute,
		screen: `${window.screen.width}x${window.screen.height} @${ratio}x`,
		viewport: `${window.innerWidth}x${window.innerHeight}`,
	}
}

async function readNativeAppInfo(): Promise<
	{ build: string; version: string } | undefined
> {
	try {
		const { App } = await import('@capacitor/app')
		return await App.getInfo()
	} catch (error) {
		console.warn('Could not read native app info', error)
		return undefined
	}
}
